import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  MessageCircle,
  Settings as SettingsIcon,
  Edit3,
  Trash2,
  Flag,
  Loader2,
  Send,
  Hash,
  Tag,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { SentimentPill, VoteBar } from '@/components/CommunityPostBits';
import CommentNode from '@/components/CommunityCommentNode';
import BookmarkButton from '@/components/BookmarkButton';
import VerifiedExpertBadge from '@/components/VerifiedExpertBadge';
import SentimentTagPicker from '@/components/SentimentTagPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return '';
  }
}

function PostGearMenu({ canEdit, canDelete, onEdit, onDelete, onReport, testidPrefix }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [open]);
  return (
    <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testidPrefix}-gear-btn`}
        aria-label="Options"
      >
        <SettingsIcon className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 w-40 rounded-md border border-border bg-popover shadow-lg overflow-hidden" data-testid={`${testidPrefix}-gear-menu`}>
          {canEdit && (
            <button type="button" className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-muted" onClick={() => { setOpen(false); onEdit(); }} data-testid={`${testidPrefix}-gear-edit`}>
              <Edit3 className="w-3.5 h-3.5" /> Edit
            </button>
          )}
          {canDelete && (
            <button type="button" className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-muted text-rose-600 dark:text-rose-400" onClick={() => { setOpen(false); onDelete(); }} data-testid={`${testidPrefix}-gear-delete`}>
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          )}
          {onReport && (
            <button type="button" className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-muted" onClick={() => { setOpen(false); onReport(); }} data-testid={`${testidPrefix}-gear-report`}>
              <Flag className="w-3.5 h-3.5" /> Report
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function PostHeader({ post, tp, onEdit, onDelete, onReport }) {
  const reportPill = post.is_admin_view && post.report_count > 0 ? (
    <span className="ml-1 px-1.5 py-0.5 bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 rounded text-[10px] font-bold">
      ⚑ {post.report_count} reports
    </span>
  ) : null;
  const deletedPill = post.deleted ? (
    <span className="px-1.5 py-0.5 bg-zinc-100 dark:bg-zinc-800 rounded text-[10px] font-bold">deleted by {post.deleted_by}</span>
  ) : null;
  const gear = (post.can_edit || post.can_delete) ? (
    <PostGearMenu
      canEdit={post.can_edit}
      canDelete={post.can_delete}
      onEdit={onEdit}
      onDelete={onDelete}
      onReport={!post.can_edit ? onReport : null}
      testidPrefix={tp}
    />
  ) : (
    <button type="button" onClick={onReport} className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-amber-500" title="Report" data-testid={`${tp}-report-btn`}>
      <Flag className="w-3.5 h-3.5" />
    </button>
  );
  return (
    <div className="flex items-center gap-2 text-[11px] text-muted-foreground mb-1 flex-wrap">
      {post.kind === 'stock' && post.ticker ? (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-violet-100 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 rounded font-bold text-xs" data-testid={`${tp}-ticker-chip`}>
          <Hash className="w-3 h-3" />{post.ticker}
        </span>
      ) : null}
      {post.kind === 'general' ? (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 rounded text-[10px] font-semibold">
          <Tag className="w-3 h-3" /> General
        </span>
      ) : null}
      {post.kind === 'tutorial' ? (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 rounded text-[10px] font-bold uppercase tracking-wide">
          📑 Tutorial
        </span>
      ) : null}
      <SentimentPill label={post.sentiment_label} score={post.sentiment_score} />
      <span>·</span>
      <span className="font-semibold text-foreground">@{post.handle}</span>
      {post.author_is_verified_expert ? <VerifiedExpertBadge showLabel={false} /> : null}
      <span>·</span>
      <span>{timeAgo(post.created_at)}</span>
      {post.edited_at ? <span className="italic">(edited)</span> : null}
      {reportPill}
      {deletedPill}
      <div className="ml-auto flex items-center gap-1">{gear}</div>
    </div>
  );
}

function PostBody({ post, tp, editOpen, editTitle, editBody, setEditTitle, setEditBody, onSave, onCancel }) {
  if (!editOpen) {
    return (
      <>
        <h3 className={post.deleted ? 'font-bold text-base italic text-muted-foreground' : 'font-bold text-base'} data-testid={`${tp}-title`}>
          {post.title}
        </h3>
        {post.body && !post.deleted ? (
          <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap line-clamp-4">{post.body}</p>
        ) : null}
      </>
    );
  }
  return (
    <div className="space-y-2">
      <input type="text" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="w-full text-sm border border-border rounded p-2 bg-background font-semibold" maxLength={200} data-testid={`${tp}-edit-title`} />
      <textarea value={editBody} onChange={(e) => setEditBody(e.target.value)} className="w-full text-sm border border-border rounded p-2 bg-background" rows={4} maxLength={8000} data-testid={`${tp}-edit-body`} />
      <div className="flex gap-2">
        <Button size="sm" onClick={onSave} data-testid={`${tp}-edit-save`}>Save</Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

function CommentsSection({ post, tp, isMember, commentBody, setCommentBody, onSubmit, commentsLoading, comments, onMutate }) {
  const composer = (isMember && !post.deleted) ? (
    <div className="flex gap-2 mb-3">
      <textarea placeholder="Write a comment…" value={commentBody} onChange={(e) => setCommentBody(e.target.value)} className="flex-1 text-sm border border-border rounded p-2 bg-background" rows={2} maxLength={4000} data-testid={`${tp}-comment-textarea`} />
      <Button size="sm" onClick={onSubmit} disabled={!commentBody.trim()} data-testid={`${tp}-comment-submit`}>
        <Send className="w-4 h-4" />
      </Button>
    </div>
  ) : null;
  if (commentsLoading) {
    return (
      <div className="mt-3 border-t border-border pt-3" data-testid={`${tp}-comments-section`}>
        {composer}
        <div className="flex justify-center py-4 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /></div>
      </div>
    );
  }
  const topLevel = comments.filter((c) => !c.parent_comment_id);
  return (
    <div className="mt-3 border-t border-border pt-3" data-testid={`${tp}-comments-section`}>
      {composer}
      {topLevel.length === 0 ? (
        <p className="text-xs text-muted-foreground py-2">No comments yet. Be the first to reply.</p>
      ) : null}
      {topLevel.map((c) => (
        <CommentNode key={c.comment_id} comment={c} allComments={comments} postId={post.post_id} onMutate={onMutate} />
      ))}
    </div>
  );
}

export default function PostCard({ post, isMember, onMutate }) {
  const [expanded, setExpanded] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentBody, setCommentBody] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editTitle, setEditTitle] = useState(post.title);
  const [editBody, setEditBody] = useState(post.body || '');
  const tp = `post-${post.post_id}`;

  const loadComments = useCallback(async () => {
    setCommentsLoading(true);
    try {
      const res = await axios.get(`${API}/community/posts/${post.post_id}/comments`, { withCredentials: true });
      setComments(res.data.comments || []);
    } catch (e) {
      toast.error('Failed to load comments.');
    } finally {
      setCommentsLoading(false);
    }
  }, [post.post_id]);

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && comments.length === 0) loadComments();
  };

  const handleVote = async (val) => {
    if (!isMember) {
      toast.error('Join the community to vote.');
      return;
    }
    try {
      await axios.post(`${API}/community/posts/${post.post_id}/vote`, { value: val }, { withCredentials: true });
      onMutate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Vote failed.');
    }
  };

  const handleComment = async () => {
    const body = commentBody.trim();
    if (!body) return;
    try {
      await axios.post(`${API}/community/posts/${post.post_id}/comments`, { body }, { withCredentials: true });
      setCommentBody('');
      toast.success('Comment posted.');
      await loadComments();
      onMutate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Comment failed.');
    }
  };

  const handleEditSubmit = async () => {
    try {
      await axios.patch(`${API}/community/posts/${post.post_id}`, { title: editTitle, body: editBody }, { withCredentials: true });
      setEditOpen(false);
      toast.success('Post updated.');
      onMutate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Edit failed.');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axios.delete(`${API}/community/posts/${post.post_id}`, { withCredentials: true });
      toast.success('Post deleted.');
      onMutate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed.');
    }
  };

  const handleReport = async () => {
    const reason = window.prompt('Report this post — reason (optional):', '');
    if (reason === null) return;
    try {
      await axios.post(`${API}/community/posts/${post.post_id}/report`, { reason }, { withCredentials: true });
      toast.success('Thanks — moderators will review.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Report failed.');
    }
  };

  const handleEditOpen = () => {
    setEditTitle(post.title);
    setEditBody(post.body || '');
    setEditOpen(true);
  };

  const cardCls = post.deleted
    ? 'rounded-xl border border-zinc-200 dark:border-zinc-800 bg-muted/30 hover:border-violet-300 dark:hover:border-violet-700 transition'
    : 'rounded-xl border border-border bg-card hover:border-violet-300 dark:hover:border-violet-700 transition';

  return (
    <div className={cardCls} data-testid={tp}>
      <div className="flex gap-3 p-4">
        <VoteBar score={post.score} viewerVote={post.viewer_vote} onVote={handleVote} vertical testidPrefix={tp} />
        <div className="flex-1 min-w-0">
          <PostHeader
            post={post}
            tp={tp}
            onEdit={handleEditOpen}
            onDelete={handleDelete}
            onReport={handleReport}
          />
          <PostBody
            post={post}
            tp={tp}
            editOpen={editOpen}
            editTitle={editTitle}
            editBody={editBody}
            setEditTitle={setEditTitle}
            setEditBody={setEditBody}
            onSave={handleEditSubmit}
            onCancel={() => setEditOpen(false)}
          />
          <div className="flex items-center gap-3 mt-2">
            <button
              type="button"
              onClick={handleToggle}
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
              data-testid={`${tp}-toggle-comments`}
            >
              <MessageCircle className="w-3.5 h-3.5" />
              {post.comment_count} {post.comment_count === 1 ? 'comment' : 'comments'}
            </button>
            {post.kind === 'tutorial' && !post.deleted ? (
              <BookmarkButton
                postId={post.post_id}
                initialBookmarked={post.viewer_bookmarked}
                initialCount={post.bookmark_count}
                isMember={isMember}
              />
            ) : null}
          </div>
          {!post.deleted ? (
            <div className="mt-2">
              <SentimentTagPicker postId={post.post_id} compact />
            </div>
          ) : null}
          {expanded ? (
            <CommentsSection
              post={post}
              tp={tp}
              isMember={isMember}
              commentBody={commentBody}
              setCommentBody={setCommentBody}
              onSubmit={handleComment}
              commentsLoading={commentsLoading}
              comments={comments}
              onMutate={loadComments}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
